// Workspaces — named navigation bars, each shown to its own groups.
//
// The MAIN workspace is the site's own bar (Settings → Navigation Bar,
// exactly as every site has today). It is always there —
// it is not created, it cannot be deleted — and it starts open to
// everyone. An admin may name it and choose who sees it. Every other
// workspace is an extra bar.
//
// ONE SAFETY RULE, shown to the admin in Settings: someone who is in none
// of the main workspace's groups AND in no other workspace still keeps the
// main bar. Nobody can be left without navigation by a groups setting.
//
// OFF UNTIL SWITCHED ON: the client config's WORKSPACES_ENABLED gates the
// whole feature. With no extra workspace a person can see, nothing changes
// for them — one bar, the logo as it always was.
//
// Navigation only: pages keep their own security. Pure: no React, no
// fetch, no storage.

export const MAIN_BAR_ID = 'main';

const visibleTo = (groups, userGroups) =>
  !groups || groups.length === 0 || (userGroups || []).some((g) => groups.includes(g));

// The main workspace as configured: its name (the site's name until an
// admin renames it) and its groups (none = everyone).
export function mainWorkspace(definition, siteName = 'Main') {
  const m = (definition && definition.main) || {};
  return { id: MAIN_BAR_ID, isMain: true, label: m.label || siteName, groups: Array.isArray(m.groups) ? m.groups : [] };
}

// definition: { main: { label, groups }, workspaces: [{ id, label, groups, logo?, title?, sidebar }] }
// → [] when this person has no extra workspace (nothing changes for them);
// otherwise the bars they may use, the main one first when they may use it.
export function visibleWorkspaces(definition, userGroups, siteName = 'Main') {
  const list = (definition && Array.isArray(definition.workspaces)) ? definition.workspaces : [];
  const extras = list.filter((w) => w && w.id && w.id !== MAIN_BAR_ID && visibleTo(w.groups, userGroups));
  if (extras.length === 0) return [];
  const main = mainWorkspace(definition, siteName);
  return visibleTo(main.groups, userGroups) ? [main, ...extras] : extras;
}

// Which bar is on: the one asked for (?ws=), else the one used last, else —
// on a first visit — the person's own extra workspace (the reason they were
// given one), else the main bar. Only ever one this person can see.
export function pickWorkspace({ visible, requested, remembered }) {
  if (!visible || visible.length === 0) return null;
  return visible.find((w) => w.id === requested)
    || visible.find((w) => w.id === remembered)
    || visible.find((w) => !w.isMain)
    || visible[0];
}

// A workspace's bar, with Settings guaranteed: an admin can never switch
// into a workspace and lose the way back to the editor. (Settings is still
// shown only in admin mode — navEntries decides that, as for every bar.)
export function workspaceSidebar(ws) {
  const bar = Array.isArray(ws && ws.sidebar) ? ws.sidebar : [];
  return bar.some((i) => i.type === 'builtin' && i.id === 'settings') ? bar : [...bar, { id: 'settings', type: 'builtin' }];
}
